import { getToken } from "../../common/environment_config.es6";
import { apiGet, authedFetch } from "../../common/http_service";

/**
 * Mindmap of the current training, for the knowledge base panel.
 *
 * The mindmap is produced by a job that runs alongside course generation, so it
 * may not exist: a course built without documents never gets one (citations are
 * document-only), and a course still generating may not have finished it yet.
 * Both cases return null — they are normal states, not errors.
 */
export class MindmapService {
  // quiet: a poll that expects "not ready yet" (404) — no server-error toast, no auth modal on 403.
  static async getTrainingMindmap(params = {}) {
    let { trainingId } = params;

    // trainingId: explicit param > global idealStudioTrainingId
    if (!trainingId) {
      trainingId = window.idealStudioTrainingId || "";
    }

    if (!trainingId) {
      throw new Error("trainingId is missing for getTrainingMindmap");
    }

    // Base URL: same pattern as sceneAudioService / trainingSummaryService
    if (!window.ideaLibraryRootUrl) {
      throw new Error("ideaLibraryRootUrl not configured for mindmap");
    }

    let baseUrl = window.ideaLibraryRootUrl;
    if (!baseUrl.toLowerCase().startsWith("http")) {
      baseUrl = window.location.origin + baseUrl;
    }

    if (!baseUrl.endsWith("/")) {
      baseUrl += "/";
    }

    // Final endpoint: {{baseUrl}}/api/mindmap/by-training/{{trainingId}}
    const url = `${baseUrl}api/mindmap/by-training/${trainingId}`;

    const headers = {};

    const token = getToken && getToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const tenantId = window.tenantId;
    if (tenantId) {
      headers["Abp.TenantId"] = tenantId;
    }

    const response = params.quiet
      ? await authedFetch(url, { headers }, { quiet: true })
      : await apiGet(url, { headers });

    // 404 is "this training has no mindmap", which the panel renders as an
    // empty state rather than an error.
    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      throw new Error(`Mindmap request failed with status ${response.status}`);
    }

    // The endpoint returns null when no row matches; an empty body parses to
    // null too, so both land on the same empty state.
    return response.json().catch(() => null);
  }
}

export default MindmapService;
